/** Shapes of admin.routes.* (packages/tenancy/src/operations/admin/routes.ts) and the draft logic of the Routes panel. */

export interface RoutePageType {
  type: string;
  label: string;
  description: string;
  placeholders: string[];
  preset: string;
  pattern: string;
  custom: boolean;
  preview: string;
}

export interface PreviousRoute {
  id: number;
  pageType: string;
  label: string;
  pattern: string;
  retiredAt: string;
  exampleFrom: string;
  exampleTo: string;
}

export interface Shadowed {
  pageType: string;
  slugs: string[];
  hiddenBy: string[];
}

export interface SiteRoutes {
  siteRef: string;
  siteName: string;
  routed: boolean;
  canEdit: boolean;
  basePath: string;
  publicUrl: string;
  reserved: string[];
  pageTypes: RoutePageType[];
  previous: PreviousRoute[];
  shadowed: Shadowed[];
}

export interface RouteChange {
  pageType: string;
  from: string;
  to: string;
  exampleFrom: string;
  exampleTo: string;
}

export interface RouteCheck {
  ok: boolean;
  issues: { pageType: string; message: string }[];
  previews: Record<string, string | null>;
  changes: RouteChange[];
  shadowed: Shadowed[];
}

export type RouteDraft = Record<string, string>;

export function draftFrom(routes: SiteRoutes): RouteDraft {
  return Object.fromEntries(routes.pageTypes.map((p) => [p.type, p.pattern]));
}

/** Only the page types whose pattern the person changed (trimmed), as the operations expect them. */
export function changedPatterns(routes: SiteRoutes, draft: RouteDraft): Record<string, string> {
  const out: Record<string, string> = {};
  for (const page of routes.pageTypes) {
    const value = (draft[page.type] ?? page.pattern).trim();
    if (value !== page.pattern) out[page.type] = value;
  }
  return out;
}

/** The issues of one page type, joined for its field. */
export function issuesFor(check: RouteCheck | null, pageType: string): string | null {
  const messages = check?.issues.filter((i) => i.pageType === pageType).map((i) => i.message) ?? [];
  return messages.length ? messages.join(' · ') : null;
}

/** The pattern with the site's base path in front, as it appears in the address bar. */
export function withBasePath(basePath: string, pattern: string): string {
  return basePath === '/' ? pattern : `${basePath}${pattern}`;
}
